/**
 * Erro tipado da porta de autenticação.
 *
 * ## Por que a mensagem já nasce em pt-BR aqui
 *
 * docs/04-estado-e-dados.md manda traduzir erro **uma vez só**, na borda. A borda
 * é o `onError` global do QueryClient, que chama `getErrorMessage(error)` e lê
 * `error.message`. Se o adapter jogasse só o código, cada hook precisaria de um
 * `switch` próprio para virar texto — e é assim que o mesmo erro acaba com três
 * redações diferentes no app.
 *
 * O `code` continua existindo porque texto não é contrato: um fluxo que precise
 * *reagir* a "e-mail já em uso" (e não só exibi-lo) compara o código, nunca a
 * string.
 *
 * ⚠️ As mensagens do app original apareciam em inglês no meio de uma interface em
 * português — "Passwords don't match!", "The email is already in use by a
 * different account", "Email required!". Isso está listado como defeito a
 * corrigir (docs/regras-de-negocio/02-autenticacao-onboarding.md §8); os textos
 * abaixo são a tradução canônica.
 */

export const AUTH_ERROR_CODES = [
  'email_already_in_use',
  'invalid_credentials',
  'account_not_found',
  'email_confirmation_required',
  'anonymous_sign_in_disabled',
  'cpf_already_in_use',
  'invalid_cpf',
  'birth_date_in_future',
] as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  email_already_in_use: 'Este e-mail já está em uso por outra conta.',
  invalid_credentials: 'E-mail ou senha incorretos.',
  account_not_found: 'Não encontramos o perfil desta conta.',
  /**
   * O cadastro criou o usuário mas o projeto exige confirmação por e-mail, então
   * não veio sessão — e sem sessão a RLS recusa a gravação do perfil. É estado de
   * configuração, não de uso: ou a confirmação é desligada no projeto, ou o fluxo
   * de cadastro passa a terminar numa tela de "confira seu e-mail".
   */
  email_confirmation_required:
    'Confirme seu e-mail para concluir o cadastro. Verifique sua caixa de entrada.',
  /**
   * Pedir socorro sem conta (DV-24) depende de "Anonymous sign-ins" ligado no
   * projeto do Supabase. Desligado, não há o que a pessoa faça na tela — é
   * configuração —, então a mensagem manda pelo caminho que funciona **agora**,
   * que é o telefone de emergência, em vez de pedir que ela tente de novo.
   */
  anonymous_sign_in_disabled:
    'Não foi possível abrir um pedido sem cadastro agora. Ligue 193 (Bombeiros) ou crie uma conta para enviar.',
  /**
   * O CPF é único (`profiles_cpf_key`): uma pessoa, uma conta. O banco é o único
   * que sabe — os perfis dos outros são invisíveis pela RLS —, então esta
   * recusa só chega depois do envio.
   */
  cpf_already_in_use: 'Este CPF já está cadastrado em outra conta.',
  /** O formulário confere os dígitos antes; esta é a rede para a versão que não conferia. */
  invalid_cpf: 'Confira o CPF: os números não formam um CPF válido.',
  birth_date_in_future: 'A data de nascimento não pode estar no futuro.',
};

export class AuthClientError extends Error {
  readonly code: AuthErrorCode;

  constructor(code: AuthErrorCode) {
    super(AUTH_ERROR_MESSAGES[code]);
    // `name` fixo dá uma stack legível; sem ele o erro aparece como "Error".
    this.name = 'AuthClientError';
    this.code = code;
  }
}

export function isAuthClientError(error: unknown): error is AuthClientError {
  return error instanceof AuthClientError;
}

/**
 * Reconhece as recusas do banco ao gravar dados pessoais em `profiles`
 * (migration `20261031100000_personal_data_on_profiles`):
 *
 * - **23505** no índice `profiles_cpf_key` — CPF já usado por outra conta;
 * - **23514** na check `profiles_cpf_is_valid` — CPF que não fecha os dígitos;
 * - **P0001** do gatilho `profiles_apply_personal_data` — nascimento no futuro.
 *
 * Exportada porque dois caminhos gravam esses campos: o cadastro (por este
 * adapter) e "Meus dados" (o service do perfil). A mensagem é uma só.
 */
export function classifyProfileWriteError(error: {
  code?: string;
  message?: string;
}): AuthErrorCode | null {
  const message = error.message ?? '';

  if (error.code === '23505' && message.includes('profiles_cpf_key')) return 'cpf_already_in_use';
  if (error.code === '23514' && message.includes('profiles_cpf_is_valid')) return 'invalid_cpf';
  if (error.code === 'P0001' && message.includes('data de nascimento')) {
    return 'birth_date_in_future';
  }

  return null;
}
